import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

export default function AdminUsers() {
  const { isSuperAdmin, isManager } = useAuth();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [resetModal, setResetModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [pwMsg, setPwMsg] = useState(null);
  const [addAdminModal, setAddAdminModal] = useState(false);
  const [adminForm, setAdminForm] = useState({ username: '', email: '', password: '', role: 'assistant' });
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminError, setAdminError] = useState('');

  const loadUsers = () => api.get('/admin/users').then(({ data }) => setUsers(data.users || []));
  const loadAdmins = () => api.get('/admin/admins').then(({ data }) => setAdmins(data.admins || []));

  useEffect(() => {
    Promise.all([loadUsers(), loadAdmins()]).finally(() => setLoading(false));
  }, []);

  const toggleStatus = async (id) => {
    try {
      const { data } = await api.put(`/admin/users/${id}/toggle-status`);
      setUsers(users.map(u => u._id === id ? { ...u, isActive: data.isActive } : u));
    } catch { alert('Error toggling status'); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user permanently?')) return;
    try { await api.delete(`/admin/users/${id}`); loadUsers(); }
    catch { alert('Error deleting user'); }
  };

  const deleteAdmin = async (id) => {
    if (!window.confirm('Delete this admin account?')) return;
    try { await api.delete(`/admin/admins/${id}`); loadAdmins(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const resetPassword = async () => {
    if (!newPassword) return;
    try {
      await api.post(`/admin/users/${resetModal}/reset-password`, { newPassword });
      setPwMsg({ type: 'success', text: '✅ Password reset!' });
      setTimeout(() => { setResetModal(null); setPwMsg(null); setNewPassword(''); }, 2000);
    } catch { setPwMsg({ type: 'error', text: 'Error resetting password' }); }
  };

  const createAdmin = async (e) => {
    e.preventDefault(); setAdminSaving(true); setAdminError('');
    try {
      await api.post('/admin/admins', adminForm);
      setAddAdminModal(false);
      setAdminForm({ username: '', email: '', password: '', role: 'assistant' });
      loadAdmins();
    } catch (err) { setAdminError(err.response?.data?.message || 'Error creating admin'); }
    finally { setAdminSaving(false); }
  };

  const updateRole = async (id, role) => {
    try { await api.put(`/admin/admins/${id}`, { role }); loadAdmins(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const filteredUsers = users.filter(u =>
    u.username?.toLowerCase().includes(search.toLowerCase()) ||
    u.email?.toLowerCase().includes(search.toLowerCase())
  );

  const roleColors = { super_admin: '#f5c400', manager: '#10b981', assistant: '#7c3aed' };
  const roleBg = { super_admin: 'rgba(245,196,0,0.1)', manager: 'rgba(16,185,129,0.1)', assistant: 'rgba(124,58,237,0.1)' };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24 }}>USERS & ADMINS</h1>
        {isSuperAdmin && (
          <button className="btn btn-primary" onClick={() => { setAddAdminModal(true); setAdminError(''); }}>+ Create Admin</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 24, background: 'var(--bg-secondary)', borderRadius: 10, padding: 4, width: 'fit-content', border: '1px solid var(--border)' }}>
        {[['users', '👥 Users', users.length], ['admins', '🛡 Admins', admins.length]].map(([key, label, count]) => (
          <button key={key} onClick={() => setTab(key)} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 14, background: tab === key ? 'var(--gradient-primary)' : 'transparent', color: tab === key ? '#0a0900' : 'var(--text-secondary)', transition: 'all 0.15s' }}>
            {label} <span style={{ background: tab === key ? 'rgba(0,0,0,0.2)' : 'var(--border)', borderRadius: 10, padding: '1px 7px', fontSize: 12, marginLeft: 4 }}>{count}</span>
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {tab === 'users' && (
        <>
          <input placeholder="Search users by name or email..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 340, marginBottom: 20 }} />
          {loading ? <div className="loading-spinner"><div className="spinner" /></div> : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                      {['User', 'Email', 'Joined', 'Last Login', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-display)', letterSpacing: '0.08em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u._id} style={{ borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-card-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#0a0900', flexShrink: 0 }}>{u.username?.[0]?.toUpperCase()}</div>
                            <span style={{ fontWeight: 600, fontSize: 14 }}>{u.username}</span>
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 13 }}>{u.email}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 13 }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                        <td style={{ padding: '12px 16px', color: 'var(--text-secondary)', fontSize: 13 }}>{u.lastLogin ? new Date(u.lastLogin).toLocaleDateString() : 'Never'}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 700, background: u.isActive ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)', color: u.isActive ? '#10b981' : '#ef4444' }}>{u.isActive ? 'Active' : 'Inactive'}</span>
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {isManager && <button className="btn btn-secondary btn-sm" onClick={() => toggleStatus(u._id)}>{u.isActive ? 'Disable' : 'Enable'}</button>}
                            {isManager && <button className="btn btn-secondary btn-sm" onClick={() => setResetModal(u._id)}>Reset PW</button>}
                            {isSuperAdmin && <button className="btn btn-danger btn-sm" onClick={() => deleteUser(u._id)}>Delete</button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>No users found</div>}
              </div>
            </div>
          )}
        </>
      )}

      {/* Admins Tab */}
      {tab === 'admins' && (
        <div>
          <div style={{ marginBottom: 16, padding: '12px 16px', background: 'rgba(245,196,0,0.06)', border: '1px solid rgba(245,196,0,0.2)', borderRadius: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
            ℹ️ <strong style={{ color: 'var(--text-primary)' }}>Admin Roles:</strong> &nbsp;
            <span style={{ color: '#f5c400' }}>★ Super Admin</span> — full control &nbsp;|&nbsp;
            <span style={{ color: '#10b981' }}>Manager</span> — manage content & users &nbsp;|&nbsp;
            <span style={{ color: '#7c3aed' }}>Assistant</span> — chat support & view only
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: 16 }}>
            {admins.map(admin => (
              <div key={admin._id} className="card" style={{ borderLeft: `3px solid ${roleColors[admin.role] || 'var(--border)'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--gradient-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 20, color: '#0a0900', flexShrink: 0 }}>{admin.username?.[0]?.toUpperCase()}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 16 }}>{admin.username}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{admin.email}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: roleBg[admin.role], color: roleColors[admin.role], textTransform: 'capitalize' }}>
                    {admin.role === 'super_admin' ? '★ Super Admin' : admin.role.charAt(0).toUpperCase() + admin.role.slice(1)}
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Since {new Date(admin.createdAt).toLocaleDateString()}</span>
                </div>
                {isSuperAdmin && admin.role !== 'super_admin' && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select value={admin.role} onChange={e => updateRole(admin._id, e.target.value)} style={{ flex: 1, fontSize: 13, padding: '6px 10px' }}>
                      <option value="assistant">Assistant</option>
                      <option value="manager">Manager</option>
                    </select>
                    <button className="btn btn-danger btn-sm" onClick={() => deleteAdmin(admin._id)}>Delete</button>
                  </div>
                )}
                {admin.role === 'super_admin' && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '6px 0' }}>★ Primary Administrator</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div className="modal-overlay" onClick={() => setResetModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setResetModal(null)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 20 }}>Reset Password</h2>
            {pwMsg && <div style={{ background: pwMsg.type === 'success' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', border: `1px solid ${pwMsg.type === 'success' ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`, borderRadius: 8, padding: '10px', color: pwMsg.type === 'success' ? '#10b981' : '#ef4444', marginBottom: 16, fontSize: 13 }}>{pwMsg.text}</div>}
            <div className="form-group">
              <label>New Password</label>
              <input type="password" placeholder="Enter new password..." value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={resetPassword} style={{ flex: 1 }}>Reset Password</button>
              <button className="btn btn-secondary" onClick={() => { setResetModal(null); setNewPassword(''); setPwMsg(null); }} style={{ flex: 1 }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Create Admin Modal */}
      {addAdminModal && (
        <div className="modal-overlay" onClick={() => setAddAdminModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setAddAdminModal(false)}>✕</button>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 8 }}>Create Admin Account</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24 }}>This account will have access to the Admin Panel. Choose the role carefully.</p>
            {adminError && <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, padding: '10px', color: '#ef4444', marginBottom: 16, fontSize: 13 }}>{adminError}</div>}
            <form onSubmit={createAdmin}>
              <div className="form-group">
                <label>Username *</label>
                <input value={adminForm.username} onChange={e => setAdminForm({ ...adminForm, username: e.target.value })} placeholder="e.g. support_agent1" required />
              </div>
              <div className="form-group">
                <label>Email *</label>
                <input type="email" value={adminForm.email} onChange={e => setAdminForm({ ...adminForm, email: e.target.value })} placeholder="agent@apexgaming.com" required />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input type="password" value={adminForm.password} onChange={e => setAdminForm({ ...adminForm, password: e.target.value })} placeholder="At least 6 characters" required />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select value={adminForm.role} onChange={e => setAdminForm({ ...adminForm, role: e.target.value })}>
                  <option value="assistant">Assistant — Chat support & view only</option>
                  <option value="manager">Manager — Manage content & users</option>
                </select>
              </div>
              <div style={{ background: 'rgba(245,196,0,0.06)', border: '1px solid rgba(245,196,0,0.2)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>
                ⚠️ <strong style={{ color: 'var(--text-primary)' }}>Note:</strong> Only Super Admins can be created via the seed script. You can create Managers and Assistants here.
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={adminSaving}>{adminSaving ? 'Creating...' : 'Create Admin'}</button>
                <button type="button" className="btn btn-secondary" onClick={() => setAddAdminModal(false)} style={{ flex: 1 }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
